<!-- ── Loading ──────────────────────────────────────────────────────────────── -->
<div *ngIf="loadState === 'loading'" class="flex items-center gap-2 py-8 text-sm text-slate-400">
  <lucide-icon name="loader" [size]="15" class="animate-spin" [strokeWidth]="2"></lucide-icon>
  Loading...
</div>

<!-- ── Error ────────────────────────────────────────────────────────────────── -->
<div *ngIf="loadState === 'error'" class="overflow-hidden rounded-xl border border-red-200 bg-red-50 px-6 py-5 text-sm text-red-700 shadow-sm">
  Failed to load {{ label }} configuration. Please refresh the page.
</div>

<!-- ══ Not configured ════════════════════════════════════════════════════════ -->
<ng-container *ngIf="loadState === 'loaded' && !config">

  <!-- Not-configured card (before clicking Configure) -->
  <div *ngIf="!showForm" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div class="flex flex-col items-center gap-5 px-8 py-10 text-center sm:flex-row sm:text-left">
      <div class="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl" [ngClass]="iconBg">
        <lucide-icon [name]="icon" [size]="22" [strokeWidth]="1.75" [ngClass]="iconColor"></lucide-icon>
      </div>
      <div class="min-w-0 flex-1">
        <p class="text-sm font-semibold text-slate-900">{{ label }}</p>
        <p class="mt-0.5 text-sm text-slate-500">{{ description }}</p>
      </div>
      <button
        *ngIf="!readOnly"
        type="button"
        (click)="startConfigure()"
        class="flex flex-shrink-0 items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
      >
        <lucide-icon name="plug-2" [size]="14" [strokeWidth]="2"></lucide-icon>
        Configure
      </button>
    </div>
  </div>

  <!-- Configuration form (after clicking Configure) -->
  <div *ngIf="showForm && !readOnly" class="space-y-5">

    <!-- Setup guide -->
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="border-b border-slate-200 px-6 py-4">
        <h2 class="text-sm font-semibold text-slate-900">How to set up {{ label }}</h2>
      </div>
      <div class="px-6 py-5">
        <p class="text-sm text-slate-600">{{ helpText }}</p>
      </div>
    </div>

    <!-- Webhook form -->
    <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
      <div class="border-b border-slate-200 px-6 py-4">
        <h2 class="text-sm font-semibold text-slate-900">Configure {{ label }} webhook</h2>
      </div>
      <div class="space-y-5 px-6 py-5">

        <!-- Webhook URL input -->
        <div>
          <label [for]="channel + '-webhook-url'" class="mb-1.5 block text-xs font-medium text-slate-700">Webhook URL</label>
          <input
            [id]="channel + '-webhook-url'"
            type="url"
            [(ngModel)]="draftUrl"
            [placeholder]="urlPlaceholder"
            class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
            [class.border-red-300]="urlError && submitAttempted"
            [class.focus:border-red-400]="urlError && submitAttempted"
            [class.focus:ring-red-200]="urlError && submitAttempted"
          />
          <p *ngIf="urlError && submitAttempted" class="mt-1.5 text-xs text-red-600">{{ urlError }}</p>
        </div>

        <!-- Enable after saving toggle -->
        <div class="flex items-center gap-3">
          <button
            type="button"
            role="switch"
            [attr.aria-checked]="draftEnabled"
            (click)="draftEnabled = !draftEnabled"
            class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2"
            [class.bg-violet-600]="draftEnabled"
            [class.bg-slate-200]="!draftEnabled"
          >
            <span
              class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
              [class.translate-x-4]="draftEnabled"
              [class.translate-x-0]="!draftEnabled"
            ></span>
          </button>
          <span class="text-sm text-slate-700">Enable after saving</span>
        </div>

        <!-- Save / cancel -->
        <div class="flex items-center gap-3 pt-1">
          <button
            type="button"
            (click)="saveConfig()"
            [disabled]="saveState === 'saving'"
            class="flex items-center gap-2 rounded-lg bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
          >
            <lucide-icon *ngIf="saveState === 'saving'" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
            {{ saveState === 'saving' ? 'Saving…' : 'Save webhook' }}
          </button>
          <button
            type="button"
            (click)="cancelForm()"
            [disabled]="saveState === 'saving'"
            class="rounded-lg border border-slate-200 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-60"
          >
            Cancel
          </button>
        </div>

      </div>
    </div>

  </div>

</ng-container>

<!-- ══ Configured ════════════════════════════════════════════════════════════ -->
<div *ngIf="loadState === 'loaded' && config" class="space-y-5">

  <!-- Test result banners -->
  <div *ngIf="testState === 'success'" class="flex items-start gap-3 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3">
    <lucide-icon name="circle-check" [size]="15" class="mt-0.5 flex-shrink-0 text-emerald-500" [strokeWidth]="2"></lucide-icon>
    <p class="flex-1 text-sm text-emerald-700">{{ testMessage }}</p>
    <button type="button" (click)="dismissTestResult()" class="flex-shrink-0 text-emerald-400 hover:text-emerald-600">
      <lucide-icon name="x" [size]="14" [strokeWidth]="2"></lucide-icon>
    </button>
  </div>
  <div *ngIf="testState === 'error'" class="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3">
    <lucide-icon name="triangle-alert" [size]="15" class="mt-0.5 flex-shrink-0 text-red-500" [strokeWidth]="2"></lucide-icon>
    <p class="flex-1 text-sm text-red-700">{{ testMessage }}</p>
    <button type="button" (click)="dismissTestResult()" class="flex-shrink-0 text-red-400 hover:text-red-600">
      <lucide-icon name="x" [size]="14" [strokeWidth]="2"></lucide-icon>
    </button>
  </div>

  <!-- Status card -->
  <div class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div class="border-b border-slate-100 px-6 py-4">
      <p class="text-xs font-semibold uppercase tracking-wide text-slate-400">{{ label }}</p>
    </div>
    <div class="flex items-center gap-4 px-6 py-5">
      <div
        class="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-lg"
        [ngClass]="iconBg"
      >
        <lucide-icon [name]="icon" [size]="19" [strokeWidth]="1.75" [ngClass]="iconColor"></lucide-icon>
      </div>
      <div class="min-w-0 flex-1">
        <div class="flex items-center gap-2">
          <p class="text-sm font-semibold text-slate-900">{{ label }}</p>
          <span
            *ngIf="config.enabled"
            class="rounded-full bg-emerald-100 px-2 py-0.5 text-[11px] font-semibold text-emerald-700"
          >Active</span>
          <span
            *ngIf="!config.enabled"
            class="rounded-full bg-slate-100 px-2 py-0.5 text-[11px] font-semibold text-slate-500"
          >Paused</span>
        </div>
        <p *ngIf="config.maskedUrl" class="mt-0.5 truncate font-mono text-xs text-slate-400">{{ config.maskedUrl }}</p>
      </div>
      <!-- Enabled toggle -->
      <button
        *ngIf="!readOnly"
        type="button"
        role="switch"
        [attr.aria-checked]="config.enabled"
        (click)="toggleEnabled()"
        [disabled]="saveState === 'saving'"
        class="relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
        [class.bg-violet-600]="config.enabled"
        [class.bg-slate-200]="!config.enabled"
      >
        <span
          class="pointer-events-none inline-block h-4 w-4 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out"
          [class.translate-x-4]="config.enabled"
          [class.translate-x-0]="!config.enabled"
        ></span>
      </button>
    </div>
  </div>

  <!-- Actions card -->
  <div *ngIf="!readOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div class="border-b border-slate-200 px-6 py-4">
      <p class="text-sm font-semibold text-slate-900">Actions</p>
    </div>
    <div class="divide-y divide-slate-100">

      <!-- Send test notification -->
      <div class="flex items-center justify-between px-6 py-4">
        <div>
          <p class="text-sm font-medium text-slate-900">Send test notification</p>
          <p class="mt-0.5 text-xs text-slate-500">Sends a sample message to verify the webhook is working.</p>
        </div>
        <button
          type="button"
          (click)="sendTest()"
          [disabled]="testState === 'testing' || !config.enabled"
          class="flex flex-shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:cursor-not-allowed disabled:opacity-60"
        >
          <lucide-icon *ngIf="testState === 'testing'" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
          <lucide-icon *ngIf="testState !== 'testing'" name="zap" [size]="14" [strokeWidth]="1.75"></lucide-icon>
          {{ testState === 'testing' ? 'Sending…' : 'Send test' }}
        </button>
      </div>

      <!-- Update URL row -->
      <div *ngIf="!showUrlInput" class="flex items-center justify-between px-6 py-4">
        <div>
          <p class="text-sm font-medium text-slate-900">Update webhook URL</p>
          <p class="mt-0.5 text-xs text-slate-500">Replace the saved webhook with a new URL.</p>
        </div>
        <button
          type="button"
          (click)="startUpdateUrl()"
          class="flex flex-shrink-0 items-center gap-2 rounded-lg border border-slate-200 bg-slate-50 px-3.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-100 focus:outline-none focus:ring-2 focus:ring-violet-300"
        >
          <lucide-icon name="link" [size]="14" [strokeWidth]="1.75"></lucide-icon>
          Update URL
        </button>
      </div>

      <!-- URL update form (inline) -->
      <div *ngIf="showUrlInput" class="px-6 py-5">
        <p class="mb-3 text-sm font-medium text-slate-900">New webhook URL</p>
        <div class="space-y-3">
          <div>
            <input
              type="url"
              [(ngModel)]="draftUrl"
              [placeholder]="urlPlaceholder"
              class="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder-slate-400 focus:border-violet-400 focus:outline-none focus:ring-2 focus:ring-violet-200"
              [class.border-red-300]="urlError && submitAttempted"
            />
            <p *ngIf="urlError && submitAttempted" class="mt-1.5 text-xs text-red-600">{{ urlError }}</p>
          </div>
          <div class="flex items-center gap-2">
            <button
              type="button"
              (click)="saveUrl()"
              [disabled]="saveState === 'saving'"
              class="flex items-center gap-2 rounded-lg bg-violet-600 px-3.5 py-2 text-sm font-semibold text-white transition-colors hover:bg-violet-700 focus:outline-none focus:ring-2 focus:ring-violet-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60"
            >
              <lucide-icon *ngIf="saveState === 'saving'" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
              {{ saveState === 'saving' ? 'Saving…' : 'Update URL' }}
            </button>
            <button
              type="button"
              (click)="cancelUpdateUrl()"
              [disabled]="saveState === 'saving'"
              class="rounded-lg border border-slate-200 bg-white px-3.5 py-2 text-sm font-medium text-slate-700 transition-colors hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-violet-300 disabled:opacity-60"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>

    </div>
  </div>

  <!-- Danger zone -->
  <div *ngIf="!readOnly" class="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
    <div class="border-b border-slate-200 px-6 py-4">
      <p class="text-xs font-semibold uppercase tracking-wide text-red-400">Danger Zone</p>
    </div>
    <div class="px-6 py-5">
      <p class="mb-4 text-sm text-slate-600">
        Removing this configuration deletes the webhook URL from KernalHQ. Notifications will stop being delivered to {{ label }}.
      </p>
      <button
        type="button"
        (click)="removeChannel()"
        [disabled]="removeState === 'removing'"
        class="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-2.5 text-sm font-medium text-red-700 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-60"
      >
        <lucide-icon *ngIf="removeState === 'removing'" name="loader" [size]="14" class="animate-spin" [strokeWidth]="2.5"></lucide-icon>
        <lucide-icon *ngIf="removeState !== 'removing'" name="unlink" [size]="14" [strokeWidth]="1.75"></lucide-icon>
        {{ removeState === 'removing' ? 'Removing…' : 'Remove ' + label }}
      </button>
    </div>
  </div>

</div>
